import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  AlertTitle,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  Stack,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  alpha,
  useTheme,
  Skeleton,
  Alert,
  Modal,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Badge,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Checkbox,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import SearchField from '../../components/SearchField';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WorkIcon from '@mui/icons-material/Work';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { BulkUpdateTaxIdModal, CSVUploadModal } from '../../components/Modal';
import { EmployeeForm } from '../../components/Forms';
import { Error } from '../../components/Error';
import { Loading } from '../../components/Loading';
import QRCode from 'qrcode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SecurityIcon from '@mui/icons-material/Security';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { preprocessDocumentImage } from '../../utils/imagePreprocess';
import * as actions from '../../redux/actions';
import { useSocket } from '../../contexts/SocketContext';

const ID_TW = 1011;
const ID_TH = 638;
const ID_TR = ID_TW / ID_TH;

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 900,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function cropToIdCard(dataUrl, bbox) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      let sx;
      let sy;
      let sw;
      let sh;
      const validBbox =
        Array.isArray(bbox) &&
        bbox.length === 4 &&
        bbox.every(
          (v) =>
            typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1,
        ) &&
        bbox[2] > bbox[0] &&
        bbox[3] > bbox[1];
      if (validBbox) {
        const [x0, y0, x1, y1] = bbox;
        sx = x0 * W;
        sy = y0 * H;
        sw = (x1 - x0) * W;
        sh = (y1 - y0) * H;
        const r = sw / sh;
        if (r > ID_TR) {
          const newH = sw / ID_TR;
          sy -= (newH - sh) / 2;
          sh = newH;
        } else {
          const newW = sh * ID_TR;
          sx -= (newW - sw) / 2;
          sw = newW;
        }
      } else {
        const r = W / H;
        if (r > ID_TR) {
          sw = H * ID_TR;
          sh = H;
          sx = (W - sw) / 2;
          sy = 0;
        } else {
          sw = W;
          sh = W / ID_TR;
          sx = 0;
          sy = (H - sh) / 2;
        }
      }
      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      if (sx + sw > W) sx = Math.max(0, W - sw);
      if (sy + sh > H) sy = Math.max(0, H - sh);
      sw = Math.min(sw, W - sx);
      sh = Math.min(sh, H - sy);
      const canvas = document.createElement('canvas');
      canvas.width = ID_TW;
      canvas.height = ID_TH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, ID_TW, ID_TH);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, ID_TW, ID_TH);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.src = dataUrl;
  });
}

const DOC_CATEGORIES = [
  { key: 'id_card', label: 'บัตรประชาชน' },
  { key: 'house_registration', label: 'ทะเบียนบ้าน' },
  { key: 'education', label: 'วุฒิการศึกษา' },
  { key: 'resume', label: 'Resume / CV' },
  { key: 'certificate', label: 'ใบรับรอง / ใบอบรม' },
  { key: 'other', label: 'เอกสารอื่นๆ' },
];

function DocCard({ url, label, onDelete }) {
  const [preview, setPreview] = useState(false);
  const isImage = /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);

  const handleClick = () => {
    if (isImage) setPreview(true);
    else window.open(url, '_blank');
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: 120,
          height: 90,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #ddd',
          cursor: 'pointer',
          flexShrink: 0,
          '&:hover': { boxShadow: 2 },
        }}
        onClick={handleClick}
      >
        {isImage ? (
          <img
            src={url}
            alt={label}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              gap: 0.5,
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Typography
              variant="caption"
              sx={{
                px: 0.5,
                textAlign: 'center',
                wordBreak: 'break-all',
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
          </Box>
        )}
        {onDelete && (
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: '#fff' },
              p: 0.3,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        )}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            px: 0.5,
            py: 0.2,
          }}
        >
          <Typography variant="caption" color="white" noWrap display="block">
            {label}
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={preview}
        onClose={() => setPreview(false)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: '#111' } }}
      >
        <DialogContent
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={url}
            alt={label}
            style={{
              maxWidth: '88vw',
              maxHeight: '78vh',
              display: 'block',
              objectFit: 'contain',
              borderRadius: 4,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
          <Typography
            variant="caption"
            color="grey.400"
            sx={{ flex: 1, ml: 1 }}
          >
            {label}
          </Typography>
          <Button
            size="small"
            onClick={() => window.open(url, '_blank')}
            sx={{ color: 'grey.300' }}
          >
            เปิดในแท็บใหม่
          </Button>
          <Button
            size="small"
            onClick={() => setPreview(false)}
            sx={{ color: 'grey.400' }}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function HrEmployee({ title, subtitle }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  const roletype = useSelector((state) => state.roletype);
  const department = useSelector((state) => state.department);
  const me = useSelector((state) => state.me);
  const role = useSelector((state) => state.role);
  const pendingEmployeeCount = useSelector(
    (state) => state.employee?.pendingCount || 0,
  );
  const socket = useSocket();
  const history = useHistory();

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [total, setTotal] = useState(undefined);
  const [selectDepartment, setSelectDepartment] = useState('');
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [bulkUpdateTaxIdOpen, setBulkUpdateTaxIdOpen] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editEmployeeData, setEditEmployeeData] = useState(null);
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editImage, setEditImage] = useState([]);
  const [editActiveTab, setEditActiveTab] = useState(0);
  const [editDocUploading, setEditDocUploading] = useState(false);
  const editDocFileRefs = useRef({});
  const editIdCardReplaceRef = useRef();
  const editIdFileRef = useRef();
  const [editIdCardPreviewOpen, setEditIdCardPreviewOpen] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [editIdScanOpen, setEditIdScanOpen] = useState(false);
  const [editIdScanImage, setEditIdScanImage] = useState(null);
  const [editIdScanMime, setEditIdScanMime] = useState('image/jpeg');
  const [editIdScanPreviewUrl, setEditIdScanPreviewUrl] = useState(null);
  const [editIdScanLoading, setEditIdScanLoading] = useState(false);
  const [editIdScanResult, setEditIdScanResult] = useState(null);
  const [editIdScanError, setEditIdScanError] = useState('');

  // Account management state
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountForm, setAccountForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  // แก้ username ของบัญชีที่มีอยู่แล้ว (เปิดมาเป็น read-only ก่อน กดปุ่มถึงจะแก้ได้)
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  const [duplicateDetailsOpen, setDuplicateDetailsOpen] = useState(false);
  const [selectedDuplicateGroup, setSelectedDuplicateGroup] = useState(null);
  const [duplicateDetailsData, setDuplicateDetailsData] = useState(null);
  const [duplicateDetailsLoading, setDuplicateDetailsLoading] = useState(false);

  // Invite state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteQR, setInviteQR] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);

  // Pending employees state
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingTab, setPendingTab] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activateData, setActivateData] = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);
  const [hrSnack, setHrSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm();

  // ── List effects ──────────────────────────────────────────────
  useEffect(() => {
    dispatch(
      actions.employeeAll({ name, page, size, selectDepartment, activeOnly }),
    );
    return () => {};
  }, [name, page, size, selectDepartment, activeOnly]);

  useEffect(() => {
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    dispatch(actions.roleAll({}));
    return () => {};
  }, []);

  useEffect(() => {
    setTotal(employee?.total);
    return () => {};
  }, [employee]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 700);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (!socket) return;
    const onSubmitted = ({ pendingCount: count }) => {
      dispatch({ type: 'EMPLOYEE_PENDING_COUNT', payload: count });
      if (pendingTab) handleOpenPendingTab();
    };
    socket.on('onboarding:submitted', onSubmitted);
    return () => socket.off('onboarding:submitted', onSubmitted);
  }, [socket, pendingTab]);

  // ── Edit dialog handlers ──────────────────────────────────────
  const handleOpenEdit = async (id) => {
    setEditOpen(true);
    setEditEmployeeId(id);
    setEditLoading(true);
    setEditEmployeeData(null);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee/${id}`,
      );
      setEditEmployeeData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenAccount = async () => {
    setAccountForm({ username: '', password: '', confirmPassword: '' });
    setAccountInfo(null);
    setShowAccountPassword(false);
    setEditingUsername(false);
    setUsernameDraft('');
    if (editEmployeeData?.uid) {
      try {
        const info = await dispatch(
          actions.userGetByEmployeeId(editEmployeeId),
        );
        setAccountInfo(info);
      } catch {
        setAccountInfo(null);
      }
    }
    setAccountOpen(true);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.password) {
      showAlert('กรุณาระบุรหัสผ่าน');
      return;
    }
    if (accountForm.password !== accountForm.confirmPassword) {
      showAlert('รหัสผ่านไม่ตรงกัน');
      return;
    }
    try {
      if (editEmployeeData?.uid) {
        await dispatch(
          actions.userPut(editEmployeeData.uid?._id || editEmployeeData.uid, {
            password: accountForm.password,
          }),
        );
        showAlert('เปลี่ยนรหัสผ่านสำเร็จ');
      } else {
        if (!accountForm.username) {
          showAlert('กรุณาระบุ Username');
          return;
        }
        await dispatch(
          actions.userLinkToEmployee(editEmployeeId, {
            username: accountForm.username,
            password: accountForm.password,
          }),
        );
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`,
        );
        setEditEmployeeData(data);
        showAlert('สร้างบัญชีผู้ใช้สำเร็จ');
      }
      setAccountOpen(false);
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด', {
        status: error?.response?.status,
      });
    }
  };

  const handleSaveUsername = async () => {
    const next = usernameDraft.trim();
    if (!next) {
      showAlert('กรุณาระบุ Username');
      return;
    }
    if (next === accountInfo?.username) {
      setEditingUsername(false);
      return;
    }
    setSavingUsername(true);
    try {
      await dispatch(
        actions.userPut(editEmployeeData.uid?._id || editEmployeeData.uid, {
          username: next,
        }),
      );
      setAccountInfo((p) => ({ ...(p || {}), username: next }));
      setEditingUsername(false);
      setUsernameDraft('');
      // sync ชื่อ user ที่อาจโชว์ในที่อื่นของ moded แก้ไขพนักงาน
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`,
        );
        setEditEmployeeData(data);
      } catch {
        /* ไม่บล็อกผลหลัก */
      }
      showAlert('เปลี่ยน Username สำเร็จ');
    } catch {
      // เปลี่ยนไม่สำเร็จ (เช่น username ซ้ำ) — ข้อความถูกโชว์โดย global error dialog แล้ว
      // ที่นี่แค่คืนช่องกลับเป็น username เดิม (ไม่เปลี่ยน)
      setEditingUsername(false);
      setUsernameDraft('');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditEmployeeId(null);
    setEditEmployeeData(null);
    setEditImage([]);
    setEditActiveTab(0);
    reset({});
  };

  const refetchEditEmployee = async () => {
    if (!editEmployeeId) return;
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`,
      );
      setEditEmployeeData(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditDocUpload = async (category, fileName, file) => {
    setEditDocUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = (e) => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await api.post(
        `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}/documents`,
        { category, label: fileName, image: dataUrl },
      );
      await refetchEditEmployee();
    } catch (e) {
      showAlert('อัปโหลดไม่สำเร็จ');
    } finally {
      setEditDocUploading(false);
    }
  };

  const handleEditDocDelete = async (docId) => {
    if (!(await confirmAsync('ลบเอกสารนี้?'))) return;
    try {
      await api.delete(
        `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}/documents/${docId}`,
      );
      await refetchEditEmployee();
    } catch (e) {
      showAlert('ลบไม่สำเร็จ');
    }
  };

  const openEditIdScanDialog = () => {
    setEditIdScanOpen(true);
    setEditIdScanImage(null);
    setEditIdScanPreviewUrl(null);
    setEditIdScanResult(null);
    setEditIdScanError('');
  };

  const resetEditIdScanState = () => {
    setEditIdScanOpen(false);
    setEditIdScanImage(null);
    setEditIdScanPreviewUrl(null);
    setEditIdScanResult(null);
    setEditIdScanError('');
  };

  const saveEditIdCardImage = (previewUrl, bbox) => {
    cropToIdCard(previewUrl, bbox).then((croppedUrl) => {
      api
        .put(`${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`, {
          id_card_image: {
            image: croppedUrl,
            imageType: 'profile',
            alt: 'id_card',
          },
        })
        .then(() => refetchEditEmployee())
        .catch((e) => console.error('id_card_image save failed', e));
    });
  };

  const handleEditIdCardImageDelete = async () => {
    if (!(await confirmAsync('ลบรูปบัตรประชาชน?'))) return;
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`,
        { id_card_image: null },
      );
      await refetchEditEmployee();
    } catch (e) {
      showAlert('ลบไม่สำเร็จ');
    }
  };

  const handleEditIdCardReplace = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      cropToIdCard(ev.target.result, null).then((croppedUrl) => {
        api
          .put(`${process.env.REACT_APP_API_URL}/employee/${editEmployeeId}`, {
            id_card_image: {
              image: croppedUrl,
              imageType: 'profile',
              alt: 'id_card',
            },
          })
          .then(() => refetchEditEmployee())
          .catch(() => showAlert('บันทึกรูปไม่สำเร็จ'));
      });
    };
    reader.readAsDataURL(file);
  };

  const handleEditIdImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditIdScanResult(null);
    setEditIdScanError('');
    setEditIdScanMime(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setEditIdScanPreviewUrl(ev.target.result);
      setEditIdScanImage(ev.target.result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleEditIdScanAnalyze = async () => {
    if (!editIdScanImage) return;
    setEditIdScanLoading(true);
    setEditIdScanError('');
    setEditIdScanResult(null);
    try {
      const base64 = await preprocessDocumentImage(
        `data:${editIdScanMime};base64,${editIdScanImage}`,
      );
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/ai/scan-id-card`,
        {
          image: base64,
          mimeType: 'image/jpeg',
        },
      );
      setEditIdScanResult(data?.result ?? data);
    } catch (err) {
      setEditIdScanError(
        err?.response?.data?.error?.message ||
          'วิเคราะห์ไม่สำเร็จ กรุณาลองใหม่',
      );
    } finally {
      setEditIdScanLoading(false);
    }
  };

  const handleEditIdScanFill = () => {
    if (!editIdScanResult) return;
    const r = editIdScanResult;
    if (r.title) setValue('title', r.title);
    if (r.firstname) setValue('firstname', r.firstname);
    if (r.lastname) setValue('lastname', r.lastname);
    if (r.firstname_th) setValue('firstname_th', r.firstname_th);
    if (r.lastname_th) setValue('lastname_th', r.lastname_th);
    if (r.tax_id) setValue('tax_id', r.tax_id);
    if (r.date_of_birth) setValue('date_of_birth', r.date_of_birth);
    if (r.gender) setValue('gender', r.gender);
    if (r.religion) setValue('religion', r.religion);
    else setValue('religion', 'ไม่ระบุ');
    if (r.address) {
      if (r.address.house_number)
        setValue('address.house_number', r.address.house_number);
      if (r.address.village_number)
        setValue('address.village_number', r.address.village_number);
      if (r.address.road) setValue('address.road', r.address.road);
      if (r.address.subdistrict)
        setValue('address.subdistrict', r.address.subdistrict);
      if (r.address.district) setValue('address.district', r.address.district);
      if (r.address.province) setValue('address.province', r.address.province);
    }
    if (editIdScanPreviewUrl)
      saveEditIdCardImage(editIdScanPreviewUrl, r.card_bbox);
    resetEditIdScanState();
  };

  const handleEditIdScanSaveOnly = () => {
    if (!editIdScanPreviewUrl) return;
    saveEditIdCardImage(editIdScanPreviewUrl, editIdScanResult?.card_bbox);
    resetEditIdScanState();
  };

  const onEditSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };
      if (!_.isEmpty(editImage)) {
        payload.image = {
          image: editImage[0]?.data_url,
          imageType: 'profile',
          alt: '',
        };
      }
      await dispatch(actions.employeePut(editEmployeeId, payload));
      showAlert('บันทึกสำเร็จ');

      // Update local state แทนการ reload
      const updatedRows = employee.rows.map((row) =>
        row.id === editEmployeeId ? { ...row, ...data } : row,
      );
      dispatch({
        type: 'EMPLOYEE_UPDATE_LOCAL',
        payload: { rows: updatedRows, total: employee.total },
      });

      handleCloseEdit();
    } catch (error) {
      console.error(error);
    }
  };

  // ── List handlers ─────────────────────────────────────────────
  const handleDeleteEmployee = async (id) => {
    const confirm = await confirmAsync('ยืนยันการลบข้อมูล');
    if (confirm) {
      try {
        await dispatch(actions.employeeDelete(id));

        // Update local state แทนการ reload
        const updatedRows = employee.rows.filter((row) => row.id !== id);
        dispatch({
          type: 'EMPLOYEE_UPDATE_LOCAL',
          payload: { rows: updatedRows, total: employee.total - 1 },
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleSelectDepartment = (event) => {
    setSelectDepartment(event.target.value);
  };

  const normalizeFullName = (firstname, lastname) => {
    const thaiPrefixes = /^(นาย|นางสาว|นาง\s*สาว|นาง|เด็กชาย|เด็กหญิง|ด\.ช\.|ด\.ญ\.|ดช\.|ดญ\.|คุณ|ท่าน)\s*/;
    const enPrefixes = /^(mr\.|mrs\.|miss|ms\.|dr\.|prof\.)\s*/i;
    return `${firstname || ''} ${lastname || ''}`
      .trim()
      .replace(thaiPrefixes, '')
      .replace(enPrefixes, '')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  };

  const handleToggleDuplicates = async () => {
    if (showDuplicates) {
      setShowDuplicates(false);
      setDuplicateGroups([]);
      return;
    }
    setDuplicatesLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee/duplicates`,
      );
      setDuplicateGroups(data || []);
      setShowDuplicates(true);
    } catch (error) {
      console.error(error);
      showAlert('เกิดข้อผิดพลาดในการตรวจสอบชื่อซ้ำ');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleOpenDuplicateDetails = async (group) => {
    setSelectedDuplicateGroup(group);
    setDuplicateDetailsOpen(true);
    setDuplicateDetailsLoading(true);
    try {
      const empIds = group.employees.map((e) => e.id);
      const {
        data,
      } = await api.post(
        `${process.env.REACT_APP_API_URL}/employee/duplicate-details`,
        { employeeIds: empIds },
      );
      if (data?.success || data) {
        setDuplicateDetailsData(data?.data || data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDuplicateDetailsLoading(false);
    }
  };

  const handleCloseDuplicateDetails = () => {
    setDuplicateDetailsOpen(false);
    setTimeout(() => {
      setSelectedDuplicateGroup(null);
      setDuplicateDetailsData(null);
    }, 300);
  };

  const handleAddFromFile = async () => {
    if (!_.isEmpty(csvData)) {
      try {
        await dispatch(actions.employeeCreate({ arr: csvData }));
        showAlert('สำเร็จ');
        setCsvUploadOpen(false);
        setCsvData([]);
        await dispatch(
          actions.employeeAll({
            name,
            page,
            size,
            selectDepartment,
            activeOnly,
          }),
        );
      } catch (error) {
        showAlert('เพิ่มไม่สำเร็จ');
        console.error(error);
      }
    } else {
      showAlert('ไม่สามารถอ่านไฟล์รายการได้');
    }
  };

  // ── Renders ───────────────────────────────────────────────────
  const renderEditDocumentsTab = () => (
    <Stack spacing={3} sx={{ pt: 2 }}>
      {editDocUploading && <LinearProgress />}
      {DOC_CATEGORIES.map(({ key, label: catLabel }) => {
        const catDocs = (editEmployeeData?.documents || []).filter(
          (d) => d.category === key,
        );
        const isIdCard = key === 'id_card';
        return (
          <Box key={key}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {catLabel}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              {isIdCard && editEmployeeData?.id_card_image?.url && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    width: 160,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 100,
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => setEditIdCardPreviewOpen(true)}
                  >
                    <img
                      src={editEmployeeData.id_card_image.url}
                      alt="บัตรประชาชน"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                  <Dialog
                    open={editIdCardPreviewOpen}
                    onClose={() => setEditIdCardPreviewOpen(false)}
                    maxWidth="lg"
                    PaperProps={{ sx: { bgcolor: '#111' } }}
                  >
                    <DialogContent
                      sx={{
                        p: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src={editEmployeeData.id_card_image.url}
                        alt="บัตรประชาชน"
                        style={{
                          maxWidth: '88vw',
                          maxHeight: '78vh',
                          display: 'block',
                          objectFit: 'contain',
                          borderRadius: 4,
                        }}
                      />
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="grey.400"
                        sx={{ flex: 1, ml: 1 }}
                      >
                        บัตรประชาชน
                      </Typography>
                      <Button
                        size="small"
                        onClick={() =>
                          window.open(
                            editEmployeeData.id_card_image.url,
                            '_blank',
                          )
                        }
                        sx={{ color: 'grey.300' }}
                      >
                        เปิดในแท็บใหม่
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setEditIdCardPreviewOpen(false)}
                        sx={{ color: 'grey.400' }}
                      >
                        ปิด
                      </Button>
                    </DialogActions>
                  </Dialog>
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      px: 1,
                      py: 0.5,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      บัตรประชาชน
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="error"
                        title="ลบรูปบัตร"
                        onClick={handleEditIdCardImageDelete}
                        sx={{
                          p: 0.4,
                          border: '1px solid',
                          borderColor: 'error.light',
                          borderRadius: 1,
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title="เปลี่ยนรูป"
                        onClick={() => editIdCardReplaceRef.current?.click()}
                        sx={{
                          p: 0.4,
                          border: '1px solid',
                          borderColor: 'primary.light',
                          borderRadius: 1,
                        }}
                      >
                        <UploadFileIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title="สแกนใหม่ด้วย AI"
                        onClick={openEditIdScanDialog}
                        sx={{
                          p: 0.4,
                          border: '1px solid',
                          borderColor: 'primary.light',
                          borderRadius: 1,
                        }}
                      >
                        <BadgeIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <input
                    ref={editIdCardReplaceRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleEditIdCardReplace}
                  />
                </Box>
              )}
              {catDocs.map((doc) => (
                <DocCard
                  key={doc._id}
                  url={doc.file_url}
                  label={doc.label}
                  onDelete={() => handleEditDocDelete(doc._id)}
                />
              ))}
              {isIdCard && (
                <Box
                  sx={{
                    width: 120,
                    height: 90,
                    border: '2px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: editDocUploading ? 'not-allowed' : 'pointer',
                    gap: 0.5,
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                  onClick={() => !editDocUploading && openEditIdScanDialog()}
                >
                  <BadgeIcon fontSize="small" />
                  <Typography variant="caption">สแกน AI</Typography>
                </Box>
              )}
              <Box
                sx={{
                  width: 120,
                  height: 90,
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: editDocUploading ? 'not-allowed' : 'pointer',
                  gap: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                }}
                onClick={() =>
                  !editDocUploading && editDocFileRefs.current[key]?.click()
                }
              >
                <UploadFileIcon fontSize="small" />
                <Typography variant="caption">อัปโหลดไฟล์</Typography>
              </Box>
              <input
                ref={(el) => {
                  editDocFileRefs.current[key] = el;
                }}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleEditDocUpload(key, file.name, file);
                  e.target.value = '';
                }}
              />
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Stack>
  );

  const renderEditIdScanDialog = () => (
    <Dialog
      open={editIdScanOpen}
      onClose={resetEditIdScanState}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <BadgeIcon color="primary" />
          <span>สแกนบัตรประชาชน</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <input
            ref={editIdFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleEditIdImageChange}
          />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => editIdFileRef.current?.click()}
          >
            เลือกรูปภาพ / ถ่ายรูปบัตร
          </Button>
          {editIdScanPreviewUrl && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={editIdScanPreviewUrl}
                alt="id-card-preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 240,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                }}
              />
            </Box>
          )}
          {editIdScanError && <Alert severity="error">{editIdScanError}</Alert>}
          {editIdScanResult && (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                ข้อมูลที่อ่านได้
              </Typography>
              {editIdScanResult.title && (
                <div>
                  คำนำหน้า: <b>{editIdScanResult.title}</b>
                </div>
              )}
              {editIdScanResult.firstname_th && (
                <div>
                  ชื่อ (TH):{' '}
                  <b>
                    {editIdScanResult.firstname_th}{' '}
                    {editIdScanResult.lastname_th}
                  </b>
                </div>
              )}
              {editIdScanResult.firstname && (
                <div>
                  ชื่อ (EN):{' '}
                  <b>
                    {editIdScanResult.firstname} {editIdScanResult.lastname}
                  </b>
                </div>
              )}
              {editIdScanResult.tax_id && (
                <div>
                  เลขบัตร: <b>{editIdScanResult.tax_id}</b>
                </div>
              )}
              {editIdScanResult.date_of_birth && (
                <div>
                  วันเกิด: <b>{editIdScanResult.date_of_birth}</b>
                </div>
              )}
              {editIdScanResult.gender && (
                <div>
                  เพศ: <b>{editIdScanResult.gender}</b>
                </div>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {editIdScanResult ? (
          <>
            <Button
              variant="contained"
              startIcon={<BadgeIcon />}
              onClick={handleEditIdScanFill}
            >
              กรอกข้อมูล + บันทึกรูป
            </Button>
            <Button variant="outlined" onClick={handleEditIdScanSaveOnly}>
              บันทึกรูปอย่างเดียว
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleEditIdScanAnalyze}
              disabled={!editIdScanImage || editIdScanLoading}
              startIcon={
                editIdScanLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <BadgeIcon />
                )
              }
            >
              {editIdScanLoading ? 'กำลังวิเคราะห์...' : 'วิเคราะห์'}
            </Button>
            {editIdScanImage && !editIdScanLoading && (
              <Button variant="outlined" onClick={handleEditIdScanSaveOnly}>
                บันทึกรูปอย่างเดียว
              </Button>
            )}
          </>
        )}
        <Button onClick={resetEditIdScanState}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );

  const renderAccountDialog = () => (
    <Dialog
      open={accountOpen}
      onClose={() => setAccountOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      {editEmployeeData?.uid ? (
        <>
          <DialogTitle fontWeight={700}>บัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box>
                <TextField
                  label="Username"
                  fullWidth
                  size="small"
                  value={
                    editingUsername
                      ? usernameDraft
                      : accountInfo?.username || '(กำลังโหลด...)'
                  }
                  onChange={(e) => setUsernameDraft(e.target.value)}
                  InputProps={{
                    readOnly: !editingUsername,
                    endAdornment: (
                      <InputAdornment position="end">
                        {editingUsername ? (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => {
                              setEditingUsername(false);
                              setUsernameDraft('');
                            }}
                            disabled={savingUsername}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ยกเลิก
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => {
                              setUsernameDraft(accountInfo?.username || '');
                              setEditingUsername(true);
                            }}
                            disabled={!accountInfo}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            เปลี่ยน
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={
                    editingUsername
                      ? undefined
                      : { '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } }
                  }
                />
                {editingUsername &&
                  usernameDraft.trim() &&
                  usernameDraft.trim() !== accountInfo?.username && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveUsername}
                        disabled={savingUsername}
                      >
                        {savingUsername ? 'กำลังบันทึก...' : 'บันทึก Username'}
                      </Button>
                    </Box>
                  )}
              </Box>
              <TextField
                label="รหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) =>
                  setAccountForm((p) => ({ ...p, password: e.target.value }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowAccountPassword((v) => !v)}
                      >
                        {showAccountPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) =>
                  setAccountForm((p) => ({
                    ...p,
                    confirmPassword: e.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">
              ปิด
            </Button>
            <Button onClick={handleSaveAccount} variant="contained">
              เปลี่ยนรหัสผ่าน
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle fontWeight={700}>สร้างบัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountForm.username}
                onChange={(e) =>
                  setAccountForm((p) => ({ ...p, username: e.target.value }))
                }
                autoComplete="off"
              />
              <TextField
                label="Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) =>
                  setAccountForm((p) => ({ ...p, password: e.target.value }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowAccountPassword((v) => !v)}
                      >
                        {showAccountPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="ยืนยัน Password"
                type={showAccountPassword ? 'text' : 'password'}
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) =>
                  setAccountForm((p) => ({
                    ...p,
                    confirmPassword: e.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAccountOpen(false)} variant="outlined">
              ยกเลิก
            </Button>
            <Button onClick={handleSaveAccount} variant="contained">
              สร้างบัญชี
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={editOpen}
      onClose={handleCloseEdit}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={600}>
            แก้ไขข้อมูลพนักงาน
            {editEmployeeData?.employee_id && (
              <Chip
                label={editEmployeeData.employee_id}
                size="small"
                variant="outlined"
                sx={{ ml: 1.5, fontWeight: 400 }}
              />
            )}
          </Typography>
          <IconButton size="small" onClick={handleCloseEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />

      <Box sx={{ bgcolor: 'grey.100', px: 3, pt: 1 }}>
        <Tabs
          value={editActiveTab}
          onChange={(_, v) => setEditActiveTab(v)}
          TabIndicatorProps={{
            style: { height: 3, borderRadius: '3px 3px 0 0' },
          }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 44,
              borderRadius: '6px 6px 0 0',
              color: 'text.secondary',
              bgcolor: 'transparent',
              mr: 0.5,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
                bgcolor: 'background.paper',
              },
            },
          }}
        >
          <Tab label="ข้อมูลทั่วไป" />
          <Tab
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>เอกสาร</span>
                {(editEmployeeData?.documents?.length > 0 ||
                  editEmployeeData?.id_card_image) && (
                  <Chip
                    label={
                      (editEmployeeData?.documents?.length || 0) +
                      (editEmployeeData?.id_card_image ? 1 : 0)
                    }
                    size="small"
                    color="primary"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {editActiveTab === 0 && (
          <>
            {editLoading && (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
            {!editLoading && editEmployeeData && (
              <EmployeeForm
                key={editEmployeeData._id}
                control={control}
                Controller={Controller}
                errors={errors}
                employee={editEmployeeData}
                roletype={roletype}
                employeeImage={editImage}
                setEmployeeImage={setEditImage}
                department={department}
                watch={watch}
                setValue={setValue}
              />
            )}
          </>
        )}
        {editActiveTab === 1 && renderEditDocumentsTab()}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color={editEmployeeData?.uid ? 'secondary' : 'inherit'}
            startIcon={<AccountCircleIcon fontSize="small" />}
            onClick={handleOpenAccount}
            size="small"
          >
            {editEmployeeData?.uid ? 'จัดการบัญชีผู้ใช้' : 'สร้าง Username'}
          </Button>
          <Chip
            label={editEmployeeData?.uid ? 'มีบัญชีแล้ว' : 'ยังไม่มีบัญชี'}
            color={editEmployeeData?.uid ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Button
          variant="outlined"
          onClick={handleCloseEdit}
          startIcon={<CloseIcon />}
        >
          ยกเลิก
        </Button>
        {editActiveTab === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmit(onEditSubmit)}
            disabled={editLoading}
            startIcon={<SaveIcon />}
          >
            บันทึก
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderCSVUploadModal = () => (
    <CSVUploadModal
      csvData={csvData}
      setCsvData={setCsvData}
      fileTemplateURL="/filetemplate/Employee.csv"
      handleAddFromFile={handleAddFromFile}
      handleClose={() => {
        setCsvUploadOpen(false);
        setCsvData([]);
      }}
      isOpen={csvUploadOpen}
      titleName="แผนก"
      typeRows={department}
      anotherComponent={
        <Box>
          <AlertTitle sx={{ mb: 2 }}>
            โปรดแทนที่ข้อมูลใน <strong>ตำแหน่ง</strong> ด้วยรหัสดังต่อไปนี้
          </AlertTitle>
          <Typography variant="subtitle2" color="primary">
            ประเภทของตำแหน่ง
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              height: 280,
              overflowY: 'auto',
              mt: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    ชื่อประเภทของตำแหน่ง
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>รหัส</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {_.map(roletype?.rows, (_type, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.05),
                      },
                    }}
                  >
                    <TableCell>{_type?.name}</TableCell>
                    <TableCell>{_type?._id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      }
    />
  );

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 3,
        gap: 2,
      }}
    >
      {/* Filter tabs */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 0.5,
          alignSelf: 'center',
        }}
      >
        <Button
          variant={activeOnly ? 'contained' : 'text'}
          color="primary"
          size="small"
          onClick={() => {
            setActiveOnly(true);
            setPage(1);
          }}
        >
          พนักงานปัจจุบัน
        </Button>
        <Button
          variant={!activeOnly ? 'contained' : 'text'}
          color="inherit"
          size="small"
          onClick={() => {
            setActiveOnly(false);
            setPage(1);
          }}
        >
          พนักงานทั้งหมด
        </Button>
      </Stack>

      {/* Add employee group */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          {/* กลุ่ม 1: พนักงานกรอกเอง */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              พนักงานกรอกเอง
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                color="secondary"
                size="small"
                onClick={() => {
                  setInviteLink('');
                  setInviteQR('');
                  setInviteOpen(true);
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                ส่งลิ้งสมัคร
              </Button>
              <Badge
                badgeContent={pendingEmployeeCount}
                color="error"
                overlap="rectangular"
              >
                <Button
                  variant={pendingTab ? 'contained' : 'outlined'}
                  startIcon={<HourglassEmptyIcon />}
                  color="warning"
                  size="small"
                  onClick={() => {
                    if (!pendingTab) handleOpenPendingTab();
                    else setPendingTab(false);
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  รอยืนยัน
                </Button>
              </Badge>
            </Stack>
          </Box>

          {/* กลุ่ม 2: HR กรอกให้ */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              HR กรอกให้
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                color="success"
                size="small"
                onClick={() => setCsvUploadOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                นำเข้า CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<BadgeIcon />}
                color="secondary"
                size="small"
                onClick={() => setBulkUpdateTaxIdOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                อัปเดตเลขบัตร/คำนำหน้า
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                color="primary"
                size="small"
                onClick={() => history.push('/humen/employee/add')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                เพิ่มด้วยตนเอง
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  const renderSearch = () => (
    <Card
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems="center"
      >
        <SearchField
          label="ค้นหาพนักงาน"
          placeholder="ชื่อ / รหัสพนักงาน / เบอร์โทร / เลขบัตรประชาชน"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <TextField
          select
          label="แผนก"
          size="small"
          sx={{ minWidth: 200 }}
          value={selectDepartment}
          onChange={handleSelectDepartment}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">ทั้งหมด</MenuItem>
          {department?.rows?.map((dept) => (
            <MenuItem key={dept._id} value={dept._id}>
              {dept.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant={showDuplicates ? 'contained' : 'outlined'}
          color="warning"
          size="small"
          startIcon={
            duplicatesLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PeopleIcon />
            )
          }
          onClick={handleToggleDuplicates}
          disabled={duplicatesLoading}
          sx={{ minWidth: 160, whiteSpace: 'nowrap' }}
        >
          {duplicatesLoading
            ? 'กำลังตรวจสอบ...'
            : showDuplicates
            ? (() => {
                const exactCount = duplicateGroups.filter(
                  (g) => g.matchType !== 'similar',
                ).length;
                const similarCount = duplicateGroups.filter(
                  (g) => g.matchType === 'similar',
                ).length;
                const parts = [];
                if (exactCount) parts.push(`ซ้ำ ${exactCount} กลุ่ม`);
                if (similarCount) parts.push(`คล้าย ${similarCount} กลุ่ม`);
                return parts.join(' | ') || 'ไม่พบ';
              })()
            : 'ตรวจสอบชื่อซ้ำ'}
        </Button>
      </Stack>
    </Card>
  );

  const renderProfileImagePreviewDialog = () => (
    <Dialog
      open={!!profileImagePreview}
      onClose={() => setProfileImagePreview(null)}
      maxWidth="lg"
      PaperProps={{ sx: { bgcolor: '#111' } }}
    >
      <DialogContent
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={profileImagePreview}
          alt="รูปโปรไฟล์"
          style={{
            maxWidth: '88vw',
            maxHeight: '78vh',
            display: 'block',
            objectFit: 'contain',
            borderRadius: 4,
          }}
        />
      </DialogContent>
      <DialogActions sx={{ bgcolor: '#111', py: 0.5 }}>
        <Typography variant="caption" color="grey.400" sx={{ flex: 1, ml: 1 }}>
          รูปโปรไฟล์
        </Typography>
        <Button
          size="small"
          onClick={() => window.open(profileImagePreview, '_blank')}
          sx={{ color: 'grey.300' }}
        >
          เปิดในแท็บใหม่
        </Button>
        <Button
          size="small"
          onClick={() => setProfileImagePreview(null)}
          sx={{ color: 'grey.400' }}
        >
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTable = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableCell width="4%">
                <Typography variant="subtitle2" fontWeight="bold">
                  ลำดับ
                </Typography>
              </TableCell>
              <TableCell width="8%">
                <Typography variant="subtitle2" fontWeight="bold">
                  รหัสพนักงาน
                </Typography>
              </TableCell>
              <TableCell width="18%">
                <Typography variant="subtitle2" fontWeight="bold">
                  ชื่อ-นามสกุล
                </Typography>
              </TableCell>
              <TableCell width="12%">
                <Typography variant="subtitle2" fontWeight="bold">
                  เลขบัตรประชาชน
                </Typography>
              </TableCell>
              <TableCell width="10%">
                <Typography variant="subtitle2" fontWeight="bold">
                  เบอร์โทรศัพท์
                </Typography>
              </TableCell>
              <TableCell width="10%">
                <Typography variant="subtitle2" fontWeight="bold">
                  วันที่เริ่มงาน
                </Typography>
              </TableCell>
              <TableCell width="10%">
                <Typography variant="subtitle2" fontWeight="bold">
                  แผนก
                </Typography>
              </TableCell>
              <TableCell width="8%">
                <Typography variant="subtitle2" fontWeight="bold">
                  ประเภทพนักงาน
                </Typography>
              </TableCell>
              <TableCell width="10%" align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  ค่าแรง/วัน
                </Typography>
              </TableCell>
              <TableCell width="10%" align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  ค่าแรง/เดือน
                </Typography>
              </TableCell>
              {me?.userData?.permissions?.hr_management && (
                <TableCell width="18%" align="right">
                  <Typography variant="subtitle2" fontWeight="bold">
                    จัดการ
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {showDuplicates ? (
              !_.isEmpty(duplicateGroups) ? (
                duplicateGroups.map((group, groupIndex) => (
                  <React.Fragment key={group.key}>
                    <TableRow>
                      <TableCell
                        colSpan={
                          me?.userData?.permissions?.hr_management ? 11 : 10
                        }
                        sx={{
                          bgcolor:
                            group.matchType === 'similar'
                              ? alpha(theme.palette.info.main, 0.08)
                              : alpha(theme.palette.error.main, 0.1),
                          py: 0.75,
                          borderLeft: `4px solid ${
                            group.matchType === 'similar'
                              ? theme.palette.info.main
                              : theme.palette.error.main
                          }`,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              size="small"
                              label={
                                group.matchType === 'similar'
                                  ? 'ชื่อคล้ายกัน'
                                  : 'ซ้ำแน่นอน'
                              }
                              color={
                                group.matchType === 'similar' ? 'info' : 'error'
                              }
                              variant="filled"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              color={
                                group.matchType === 'similar'
                                  ? 'info.dark'
                                  : 'error.dark'
                              }
                            >
                              {`กลุ่มที่ ${groupIndex + 1} — ${
                                group.employees.length
                              } รายการ: "${group.key}"`}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            color={
                              group.matchType === 'similar' ? 'info' : 'error'
                            }
                            sx={{ boxShadow: 'none', flexShrink: 0 }}
                            onClick={() => handleOpenDuplicateDetails(group)}
                          >
                            ตรวจสอบรายละเอียด
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {group.employees.map((row, index) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(
                              group.matchType === 'similar'
                                ? theme.palette.info.light
                                : theme.palette.error.light,
                              0.08,
                            ),
                          },
                          borderLeft: `4px solid ${alpha(
                            group.matchType === 'similar'
                              ? theme.palette.info.main
                              : theme.palette.error.main,
                            0.3,
                          )}`,
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row?.employee_id || '-'}
                            color="default"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Avatar
                              alt={formatEmployeeThaiName(row)}
                              src={row.profile_image_url || row.image_url}
                              onClick={(e) => {
                                const url =
                                  row.profile_image_url || row.image_url;
                                if (!url) return;
                                e.stopPropagation();
                                setProfileImagePreview(url);
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: theme.palette.warning.main,
                                cursor:
                                  row.profile_image_url || row.image_url
                                    ? 'pointer'
                                    : 'default',
                              }}
                            >
                              {(row.firstname_th || row.firstname)?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2">
                              {formatEmployeeThaiName(row)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.tax_id || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.phone_number ? (
                            <Typography variant="body2">
                              {row.phone_number}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontStyle="italic"
                            >
                              ไม่ระบุ
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const d = row.date_start || row.createdAt;
                            const isEstimated = !row.date_start;
                            return d ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color={
                                    isEstimated
                                      ? 'text.secondary'
                                      : 'text.primary'
                                  }
                                >
                                  {new Date(d).toLocaleDateString('th-TH', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </Typography>
                                {isEstimated && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    จากวันบันทึก
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {row?.department?.name ? (
                            <Chip
                              size="small"
                              label={row.department.name}
                              color="warning"
                              variant="outlined"
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontStyle="italic"
                            >
                              ไม่ระบุแผนก
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              row?.type === 'DAILY' ? 'รายวัน' : 'รายเดือน'
                            }
                            color={row?.type === 'DAILY' ? 'info' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row?.salary?.month ? (
                            <Typography variant="body2">
                              {(Number(row.salary.month) / 30).toLocaleString(
                                'th-TH',
                                {
                                  maximumFractionDigits: 0,
                                },
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row?.salary?.month ? (
                            <Typography variant="body2">
                              {Number(row.salary.month).toLocaleString('th-TH')}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        {me?.userData?.permissions?.hr_management && (
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  history.push(
                                    `/humen/employee/detail/${row.id}`,
                                  );
                                }}
                              >
                                รายละเอียด
                              </Button>
                              <Tooltip title="แก้ไขข้อมูล">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEdit(row.id);
                                  }}
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.warning.main,
                                      0.1,
                                    ),
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.warning.main,
                                        0.2,
                                      ),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" color="warning" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="ลบพนักงาน">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEmployee(row?.id);
                                  }}
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.1,
                                    ),
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        0.2,
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box
                      sx={{
                        py: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <PeopleIcon
                        sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                      />
                      <Typography variant="h6" color="success.main">
                        ไม่พบชื่อซ้ำในระบบ
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )
            ) : !_.isEmpty(employee.rows) ? (
              employee.rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  onDoubleClick={() => handleOpenEdit(row.id)}
                  sx={{
                    cursor: 'pointer',
                    '&:nth-of-type(odd)': {
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.light, 0.05),
                    },
                  }}
                >
                  <TableCell>{(page - 1) * size + index + 1}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row?.employee_id || '-'}
                      color="default"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        alt={formatEmployeeThaiName(row)}
                        src={row.profile_image_url || row.image_url}
                        imgProps={{ loading: 'lazy' }}
                        onClick={(e) => {
                          const url = row.profile_image_url || row.image_url;
                          if (!url) return;
                          e.stopPropagation();
                          setProfileImagePreview(url);
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                          cursor:
                            row.profile_image_url || row.image_url
                              ? 'pointer'
                              : 'default',
                        }}
                      >
                        {(row.firstname_th || row.firstname)?.charAt(0)}
                      </Avatar>
                      <Typography variant="body2">
                        {formatEmployeeThaiName(row)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.tax_id || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.phone_number ? (
                      <Typography variant="body2">
                        {row.phone_number}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        ไม่ระบุ
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const d = row.date_start || row.createdAt;
                      const isEstimated = !row.date_start;
                      return d ? (
                        <Box>
                          <Typography
                            variant="body2"
                            color={
                              isEstimated ? 'text.secondary' : 'text.primary'
                            }
                          >
                            {new Date(d).toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                          {isEstimated && (
                            <Typography variant="caption" color="text.disabled">
                              จากวันบันทึก
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {row?.department?.name ? (
                      <Chip
                        size="small"
                        label={row?.department?.name}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 400 }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        ไม่ระบุแผนก
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row?.type === 'DAILY' ? 'รายวัน' : 'รายเดือน'}
                      color={row?.type === 'DAILY' ? 'info' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {row?.salary?.month ? (
                      <Typography variant="body2">
                        {(Number(row.salary.month) / 30).toLocaleString(
                          'th-TH',
                          { maximumFractionDigits: 0 },
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row?.salary?.month ? (
                      <Typography variant="body2">
                        {Number(row.salary.month).toLocaleString('th-TH')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  {me?.userData?.permissions?.hr_management && (
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/humen/employee/detail/${row.id}`);
                          }}
                        >
                          รายละเอียด
                        </Button>
                        <Tooltip title="แก้ไขข้อมูล">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(row.id);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.warning.main, 0.2),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" color="warning" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="ลบพนักงาน">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEmployee(row?.id);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11}>
                  <Box
                    sx={{
                      py: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PersonAddIcon
                      sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      ไม่พบข้อมูลพนักงาน
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ mt: 1, mb: 2 }}
                    >
                      {searchTerm || selectDepartment
                        ? 'ไม่พบข้อมูลที่ตรงกับเงื่อนไขการค้นหา'
                        : activeOnly
                        ? 'ไม่มีพนักงานที่ active ใน 15 วันล่าสุด ลองดูที่ "พนักงานทั้งหมด"'
                        : 'ยังไม่มีข้อมูลพนักงานในระบบ'}
                    </Typography>
                    {!searchTerm && !selectDepartment && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => history.push('/humen/employee/add')}
                      >
                        เพิ่มพนักงาน
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <TablePagination
        component="div"
        count={total || 0}
        page={page - 1}
        rowsPerPage={size}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100, 200]}
        labelRowsPerPage="แถวต่อหน้า:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
        }
      />
    </Card>
  );

  const renderSkeletonTable = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              {['5%', '10%', '30%', '15%', '20%', '20%'].map((w, i) => (
                <TableCell key={i} width={w}>
                  <Skeleton variant="text" width="60%" />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: Math.min(size, 10) }).map((_, i) => (
              <TableRow
                key={i}
                sx={{
                  '&:nth-of-type(odd)': {
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                  },
                }}
              >
                <TableCell>
                  <Skeleton variant="text" width={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rounded" width={70} height={24} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width={120} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={90} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rounded" width={80} height={24} />
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                  >
                    <Skeleton variant="rounded" width={72} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width={300} height={36} />
      </Box>
    </Card>
  );

  // ── Invite handlers ──────────────────────────────────────────────
  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    setInviteLink('');
    setInviteQR('');
    setInviteCopied(false);
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/onboarding/invite`,
      );
      const token = data?.result?.token || data?.token;
      const link = `${window.location.origin}/join/${token}`;
      setInviteLink(link);
      const qrUrl = await QRCode.toDataURL(link, { width: 240, margin: 2 });
      setInviteQR(qrUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const handleOpenPendingTab = async () => {
    setPendingTab(true);
    setPendingLoading(true);
    try {
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/onboarding/pending`,
      );
      setPendingList(data?.result?.rows || data?.rows || []);
    } catch (err) {
      console.error(err);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleOpenActivate = (emp) => {
    const today = new Date().toISOString().split('T')[0];
    setActivateData({
      ...emp,
      department: emp.department?._id || '',
      role: emp.role?._id || '',
      date_start: today,
      type: emp.type || 'FULLTIME',
      salary_month: emp.salary?.month || '',
      salary_extra_month: emp.salary_extra?.month || '',
      salary_extra_day: emp.salary_extra?.day || '',
      field_allowance_enabled: emp.field_allowance?.enabled || false,
      field_allowance_amount: emp.field_allowance?.override_amount ?? '',
      sso_status: emp.sso_tax?.status ?? true,
      tax_status: emp.tax?.status ?? false,
      tax_rate: emp.tax?.number ?? 3,
      timestamp_use_location: emp.timestamp_use_location ?? false,
      require_checkin_photo: emp.require_checkin_photo ?? true,
      project_access_mode: emp.project_access_mode ?? false,
      system_role: (emp.system_role || [])
        .map((r) => r?.id || r?._id || String(r))
        .filter(Boolean),
    });
    setActivateOpen(true);
  };

  const handleActivate = async () => {
    if (!activateData) return;
    setActivateLoading(true);
    try {
      await api.put(
        `${process.env.REACT_APP_API_URL}/onboarding/activate/${activateData._id}`,
        {
          department: activateData.department,
          role: activateData.role,
          date_start: activateData.date_start,
          type: activateData.type,
          salary: { month: Number(activateData.salary_month) || 0 },
          salary_extra: {
            month: Number(activateData.salary_extra_month) || 0,
            day: Number(activateData.salary_extra_day) || 0,
          },
          field_allowance: {
            enabled: activateData.field_allowance_enabled,
            override_amount:
              activateData.field_allowance_amount !== ''
                ? Number(activateData.field_allowance_amount)
                : null,
          },
          sso_tax: { status: activateData.sso_status },
          tax: {
            status: activateData.tax_status,
            number: Number(activateData.tax_rate) || 3,
          },
          timestamp_use_location: activateData.timestamp_use_location,
          require_checkin_photo: activateData.require_checkin_photo,
          project_access_mode: activateData.project_access_mode,
          system_role: activateData.system_role || [],
        },
      );
      setActivateOpen(false);
      setPendingList((prev) => prev.filter((e) => e._id !== activateData._id));
      dispatch(
        actions.employeeAll({ name, page, size, selectDepartment, activeOnly }),
      );
      dispatch(actions.employeePendingCount());
      setHrSnack({
        open: true,
        message: `ยืนยันพนักงาน ${formatEmployeeThaiName(
          activateData,
        )} เรียบร้อยแล้ว`,
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      const msg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'เกิดข้อผิดพลาด กรุณาลองใหม่';
      if (
        err.response?.status === 400 &&
        msg.includes('ไม่ได้อยู่ในสถานะ pending')
      ) {
        setActivateOpen(false);
        setPendingList((prev) =>
          prev.filter((e) => e._id !== activateData._id),
        );
        dispatch(
          actions.employeeAll({
            name,
            page,
            size,
            selectDepartment,
            activeOnly,
          }),
        );
        setHrSnack({
          open: true,
          message: 'พนักงานคนนี้ถูกยืนยันเรียบร้อยแล้วก่อนหน้านี้',
          severity: 'info',
        });
      } else {
        setHrSnack({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setActivateLoading(false);
    }
  };

  const renderInviteDialog = () => (
    <Dialog
      open={inviteOpen}
      onClose={() => setInviteOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontWeight="bold">สร้าง Invite Link</Typography>
          <IconButton size="small" onClick={() => setInviteOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          ส่งลิ้งหรือ QR Code นี้ให้พนักงานใหม่กรอกข้อมูลส่วนตัว —
          ใช้ได้ครั้งเดียวเท่านั้น
        </Typography>
        {!inviteLink && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleGenerateInvite}
            disabled={inviteLoading}
            startIcon={
              inviteLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <LinkIcon />
              )
            }
          >
            {inviteLoading ? 'กำลังสร้าง...' : 'สร้าง Link'}
          </Button>
        )}
        {inviteLink && (
          <Box>
            {inviteQR && (
              <Box display="flex" justifyContent="center" mb={2}>
                <img
                  src={inviteQR}
                  alt="QR Code"
                  style={{ borderRadius: 8, border: '1px solid #eee' }}
                />
              </Box>
            )}
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                wordBreak: 'break-all',
                mb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {inviteLink}
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyInviteLink}
              color={inviteCopied ? 'success' : 'primary'}
            >
              {inviteCopied ? 'คัดลอกแล้ว!' : 'คัดลอก Link'}
            </Button>
            <Alert severity="warning" sx={{ mt: 2, fontSize: 12 }}>
              Link นี้ใช้ได้ครั้งเดียว เมื่อพนักงานกรอกข้อมูลแล้วจะใช้ซ้ำไม่ได้
            </Alert>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  const renderActivateDialog = () => (
    <Dialog
      open={activateOpen}
      onClose={() => setActivateOpen(false)}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgb(var(--color-primary-900)) 0%, rgb(var(--color-primary-500)) 100%)',
          px: 3,
          py: 2.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={activateData?.profile_image_url}
              sx={{
                width: 48,
                height: 48,
                border: '2px solid rgba(255,255,255,0.4)',
                fontSize: 20,
              }}
            >
              {(activateData?.firstname_th || activateData?.firstname)?.charAt(
                0,
              )}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="#fff">
                {formatEmployeeThaiName(activateData)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.65)' }}
              >
                กรอกข้อมูลเพื่อยืนยันเป็นพนักงาน
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => setActivateOpen(false)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        <Stack spacing={0}>
          {/* เอกสารที่แนบมา */}
          {activateData?.documents?.length > 0 && (
            <Box
              sx={{
                px: 3,
                py: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <InsertDriveFileIcon
                  sx={{ fontSize: 16, color: 'info.main' }}
                />
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  เอกสารที่แนบมา
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {activateData.documents.map((doc, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {doc.label || doc.category}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      href={doc.file_url}
                      target="_blank"
                      sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
                    >
                      ดู
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* Section: การจ้างงาน */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WorkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                การจ้างงาน
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>ประเภทพนักงาน</InputLabel>
                  <Select
                    label="ประเภทพนักงาน"
                    value={activateData?.type || 'FULLTIME'}
                    onChange={(e) =>
                      setActivateData((p) => ({ ...p, type: e.target.value }))
                    }
                  >
                    <MenuItem value="FULLTIME">Full-time</MenuItem>
                    <MenuItem value="DAILY">Daily Worker</MenuItem>
                    <MenuItem value="CONTRACT">Contract Worker</MenuItem>
                    <MenuItem value="OUTSOURCE">Outsource</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="วันที่เริ่มงาน"
                  type="date"
                  size="small"
                  value={activateData?.date_start || ''}
                  onChange={(e) =>
                    setActivateData((p) => ({
                      ...p,
                      date_start: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>แผนก</InputLabel>
                  <Select
                    label="แผนก"
                    value={activateData?.department || ''}
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        department: e.target.value,
                      }))
                    }
                  >
                    {department?.rows?.map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>ตำแหน่ง</InputLabel>
                  <Select
                    label="ตำแหน่ง"
                    value={activateData?.role || ''}
                    onChange={(e) =>
                      setActivateData((p) => ({ ...p, role: e.target.value }))
                    }
                  >
                    {roletype?.rows?.map((r) => (
                      <MenuItem key={r._id} value={r._id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Section: เงินเดือน */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                เงินเดือน
              </Typography>
            </Stack>
            <TextField
              fullWidth
              label="เงินเดือน (บาท/เดือน)"
              type="number"
              size="small"
              value={activateData?.salary_month || ''}
              onChange={(e) =>
                setActivateData((p) => ({ ...p, salary_month: e.target.value }))
              }
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="text.secondary">
                      ฿
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Section: การลงเวลา */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                การลงเวลา
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>รูปแบบลงเวลา</InputLabel>
                  <Select
                    label="รูปแบบลงเวลา"
                    value={
                      activateData?.timestamp_use_location
                        ? 'location'
                        : 'manual'
                    }
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        timestamp_use_location: e.target.value === 'location',
                      }))
                    }
                  >
                    <MenuItem value="manual">เลือกโครงการเอง</MenuItem>
                    <MenuItem value="location">GPS (1 กม.)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>ถ่ายรูปลงเวลา</InputLabel>
                  <Select
                    label="ถ่ายรูปลงเวลา"
                    value={
                      activateData?.require_checkin_photo === false
                        ? 'no'
                        : 'yes'
                    }
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        require_checkin_photo: e.target.value !== 'no',
                      }))
                    }
                  >
                    <MenuItem value="yes">ต้องถ่ายรูป</MenuItem>
                    <MenuItem value="no">ไม่ต้องถ่ายรูป</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>สิทธิ์โครงการ</InputLabel>
                  <Select
                    label="สิทธิ์โครงการ"
                    value={
                      activateData?.project_access_mode ? 'specific' : 'all'
                    }
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        project_access_mode: e.target.value === 'specific',
                      }))
                    }
                  >
                    <MenuItem value="all">ทุกโครงการ</MenuItem>
                    <MenuItem value="specific">เฉพาะที่กำหนด</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Section: ค่าออกสนาม */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DirectionsCarIcon
                  sx={{ fontSize: 16, color: 'secondary.main' }}
                />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                ค่าออกสนาม
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <FormControlLabel
                sx={{ mt: 0.5, flex: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!activateData?.field_allowance_enabled}
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        field_allowance_enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Typography variant="body2">มีสิทธิ์ได้ค่าออกสนาม</Typography>
                }
              />
              <TextField
                label="บาท/วัน"
                type="number"
                size="small"
                sx={{ width: 140 }}
                value={activateData?.field_allowance_amount ?? ''}
                onChange={(e) =>
                  setActivateData((p) => ({
                    ...p,
                    field_allowance_amount: e.target.value,
                  }))
                }
                inputProps={{ min: 0 }}
                helperText="เว้นว่าง = ส่วนกลาง"
                disabled={!activateData?.field_allowance_enabled}
              />
            </Stack>
          </Box>

          {/* Section: ภาษี & ประกันสังคม */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountBalanceIcon
                  sx={{ fontSize: 16, color: 'error.main' }}
                />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                ภาษี & ประกันสังคม
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!activateData?.sso_status}
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        sso_status: e.target.checked,
                      }))
                    }
                  />
                }
                label={<Typography variant="body2">ขึ้นประกันสังคม</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!activateData?.tax_status}
                    onChange={(e) =>
                      setActivateData((p) => ({
                        ...p,
                        tax_status: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Typography variant="body2">หักภาษี ณ ที่จ่าย</Typography>
                }
              />
              <TextField
                label="อัตรา SSO (%)"
                type="number"
                size="small"
                sx={{ width: 130 }}
                value={activateData?.tax_rate ?? 3}
                onChange={(e) =>
                  setActivateData((p) => ({ ...p, tax_rate: e.target.value }))
                }
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>

          {/* Section: บทบาทในระบบ */}
          <Box sx={{ px: 3, py: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 16, color: 'info.main' }} />
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
              >
                บทบาทในระบบ
              </Typography>
            </Stack>
            <Autocomplete
              multiple
              size="small"
              options={role?.rows || []}
              getOptionLabel={(o) => o.name || ''}
              value={(role?.rows || []).filter((r) =>
                (activateData?.system_role || []).includes(r.id || r._id),
              )}
              onChange={(_, val) =>
                setActivateData((p) => ({
                  ...p,
                  system_role: val.map((r) => r.id || r._id),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="บทบาท"
                  placeholder="เลือกบทบาท..."
                />
              )}
              renderTags={(val, getTagProps) =>
                val.map((r, i) => (
                  <Chip
                    key={r.id || r._id}
                    label={r.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    {...getTagProps({ index: i })}
                  />
                ))
              }
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={() => setActivateOpen(false)}
          color="inherit"
          variant="outlined"
          size="small"
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleActivate}
          disabled={activateLoading}
          startIcon={
            activateLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PersonAddIcon />
            )
          }
          sx={{ px: 3 }}
        >
          {activateLoading ? 'กำลังบันทึก...' : 'ยืนยันเป็นพนักงาน'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPendingSection = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'warning.light',
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HourglassEmptyIcon color="warning" fontSize="small" />
          <Typography fontWeight="bold" color="warning.dark">
            รอ HR กรอกข้อมูล ({pendingList.length})
          </Typography>
        </Stack>
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            setPendingTab(false);
          }}
        >
          ซ่อน
        </Button>
      </Box>
      {pendingLoading ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : pendingList.length === 0 ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary" variant="body2">
            ไม่มีรายการที่รอดำเนินการ
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}
              >
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ชื่อ-นามสกุล
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    เบอร์โทร
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    เลขบัตรประชาชน
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    วันที่กรอก
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight="bold">
                    จัดการ
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingList.map((emp) => (
                <TableRow key={emp._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        src={emp.profile_image_url}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {formatEmployeeThaiName(emp)}
                        </Typography>
                        {emp.pending_username && (
                          <Typography variant="caption" color="text.secondary">
                            @{emp.pending_username}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {emp.phone_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{emp.tax_id || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {emp.createdAt
                        ? new Date(emp.createdAt).toLocaleDateString('th-TH')
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={() => handleOpenActivate(emp)}
                    >
                      กรอกข้อมูล HR
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );

  const renderDuplicateDetailsModal = () => (
    <Modal
      open={duplicateDetailsOpen}
      onClose={handleCloseDuplicateDetails}
      closeAfterTransition
    >
      <Fade in={duplicateDetailsOpen}>
        <Box sx={modalStyle}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h2" fontWeight="bold">
              <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ข้อมูลเปรียบเทียบพนักงานที่ชื่อซ้ำ
            </Typography>
            <IconButton
              size="small"
              onClick={handleCloseDuplicateDetails}
              sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          {duplicateDetailsLoading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography>กำลังโหลดข้อมูล...</Typography>
            </Box>
          ) : duplicateDetailsData && selectedDuplicateGroup ? (
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {selectedDuplicateGroup.employees.map((emp) => {
                const stats = duplicateDetailsData[emp.id] || {};

                const formatDate = (dateStr) => {
                  if (!dateStr) return '-';
                  return new Date(dateStr).toLocaleDateString('th-TH', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  });
                };

                return (
                  <Accordion
                    key={emp.id}
                    sx={{
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Chip
                            size="small"
                            label={emp.employee_id || '-'}
                            color="primary"
                          />
                          <Typography fontWeight="bold">
                            {formatEmployeeThaiName(emp)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {stats.timestamp > 0 && (
                            <Chip
                              size="small"
                              icon={<AccessTimeIcon />}
                              label={`${stats.timestamp} ลงเวลา`}
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {stats.expenses > 0 && (
                            <Chip
                              size="small"
                              icon={<AttachMoneyIcon />}
                              label={`${stats.expenses} เบิกจ่าย`}
                              color="info"
                              variant="outlined"
                            />
                          )}
                          {stats.ot > 0 && (
                            <Chip
                              size="small"
                              icon={<EventIcon />}
                              label={`${stats.ot} OT`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {stats.salary > 0 && (
                            <Chip
                              size="small"
                              icon={<ReceiptIcon />}
                              label={`${stats.salary} เงินเดือน`}
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                          {stats.project > 0 && (
                            <Chip
                              size="small"
                              icon={<WorkIcon />}
                              label={`${stats.project} โปรเจค`}
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              เลขบัตรประชาชน
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {emp.tax_id || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              เบอร์โทรศัพท์
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {emp.phone_number || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              แผนก
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {emp?.department?.name || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              วันที่เริ่มงาน
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(emp.date_start || emp.createdAt)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            sx={{
                              mb: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              pb: 0.5,
                            }}
                          >
                            ประวัติลงเวลา (ล่าสุด 5 รายการจาก{' '}
                            {stats.timestamp || 0})
                          </Typography>
                          {stats.timestampDocs?.length > 0 ? (
                            <List dense disablePadding>
                              {stats.timestampDocs.map((doc, idx) => (
                                <ListItem key={idx} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <AccessTimeIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`เข้า: ${
                                      doc.checkIn
                                        ? formatDate(doc.checkIn)
                                        : '-'
                                    } | ออก: ${
                                      doc.checkOut
                                        ? formatDate(doc.checkOut)
                                        : '-'
                                    }`}
                                    secondary={
                                      doc.project_in?.name ||
                                      doc.note ||
                                      'ไม่มีหมายเหตุ'
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              ไม่มีข้อมูลลงเวลา
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="info.main"
                            sx={{
                              mb: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              pb: 0.5,
                            }}
                          >
                            ประวัติเบิกจ่าย (ล่าสุด 5 รายการจาก{' '}
                            {stats.expenses || 0})
                          </Typography>
                          {stats.expensesDocs?.length > 0 ? (
                            <List dense disablePadding>
                              {stats.expensesDocs.map((doc, idx) => (
                                <ListItem key={idx} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <AttachMoneyIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`${doc.name || 'ไม่ระบุ'} - ฿${
                                      doc.net_price?.toLocaleString() || 0
                                    }`}
                                    secondary={formatDate(
                                      doc.date || doc.createdAt,
                                    )}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              ไม่มีข้อมูลเบิกจ่าย
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="warning.main"
                            sx={{
                              mb: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              pb: 0.5,
                            }}
                          >
                            ประวัติขอ OT (ล่าสุด 5 รายการจาก {stats.ot || 0})
                          </Typography>
                          {stats.otRequestDocs?.length > 0 ? (
                            <List dense disablePadding>
                              {stats.otRequestDocs.map((doc, idx) => (
                                <ListItem key={idx} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <EventIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`วันที่: ${formatDate(
                                      doc.date,
                                    )} (${doc.total_hours || 0} ชม.)`}
                                    secondary={`สถานะ: ${
                                      doc.status === 1
                                        ? 'อนุมัติแล้ว'
                                        : 'รออนุมัติ'
                                    } | ${doc.description || ''}`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              ไม่มีข้อมูลขอ OT
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography
                            variant="subtitle2"
                            color="secondary.main"
                            sx={{
                              mb: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              pb: 0.5,
                            }}
                          >
                            ประวัติเงินเดือน (ล่าสุด 5 รายการจาก{' '}
                            {stats.salary || 0})
                          </Typography>
                          {stats.salaryDocs?.length > 0 ? (
                            <List dense disablePadding>
                              {stats.salaryDocs.map((doc, idx) => (
                                <ListItem key={idx} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <ReceiptIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`รอบ: ${formatDate(
                                      doc.dateStart,
                                    )} - ${formatDate(doc.dateEnd)}`}
                                    secondary={`รับสุทธิ: ฿${
                                      doc.total?.toLocaleString() || 0
                                    }`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              ไม่มีข้อมูลเงินเดือน
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography>ไม่มีข้อมูล</Typography>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleCloseDuplicateDetails}>
              ปิดหน้าต่าง
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  if (!employee.isCompleted && !employee.isLoading) {
    return <Error />;
  }

  return (
    <div>
      <Snackbar
        open={hrSnack.open}
        autoHideDuration={5000}
        onClose={() => setHrSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={hrSnack.severity}
          onClose={() => setHrSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {hrSnack.message}
        </Alert>
      </Snackbar>
      {renderDuplicateDetailsModal()}
      {renderEditDialog()}
      {renderAccountDialog()}
      {renderEditIdScanDialog()}
      {renderCSVUploadModal()}
      <BulkUpdateTaxIdModal
        open={bulkUpdateTaxIdOpen}
        onClose={() => setBulkUpdateTaxIdOpen(false)}
        onApplied={() =>
          dispatch(
            actions.employeeAll({
              name,
              page,
              size,
              selectDepartment,
              activeOnly,
            }),
          )
        }
      />
      {renderInviteDialog()}
      {renderActivateDialog()}
      {renderProfileImagePreviewDialog()}
      {renderHeader()}
      {renderSearch()}
      {pendingTab && renderPendingSection()}
      {employee.isLoading ? renderSkeletonTable() : renderTable()}
    </div>
  );
}

HrEmployee.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

HrEmployee.defaultProps = {
  title: '',
  subtitle: '',
};

export default HrEmployee;
